import { z } from 'zod'

// ─── WireGameStateSchema ──────────────────────────────────────────────────────
// Mirrors the GameState interface in index.ts — only the fields that actually
// reach the wire via normaliseStateForWire / formatGame.
//
// CRITICAL: rejects the `map` field (the full MapDef).  That field is stripped
// by normaliseStateForWire before the payload is sent.  Its presence on the
// wire is the Tier-3 regression; the z.never().optional() guard fails the
// parse immediately when it appears, surfacing the bug in tests.
//
// .passthrough() is intentional: new engine-internal fields added to GameState
// will not break existing parsers until they're explicitly added here.

const TerritoryWireSchema = z.union([
  z.object({
    id: z.string(),
    ownerId: z.string().nullable(),
    armies: z.number(),
  }),
  // Fog of war: outside the viewer's vision. Heavy fog omits the owner too.
  z.object({
    id: z.string(),
    ownerId: z.string().optional(),
    fogged: z.literal(true),
  }),
])

export const WireGameStateSchema = z
  .object({
    mapId: z.string(),
    territories: z.array(TerritoryWireSchema).optional(),
    phase: z.enum(['claim', 'placement', 'attack', 'fortify', 'post-elim-placement']),
    turnIndex: z.number(),
    players: z.array(z.string()),
    eliminated: z.array(z.string()),
    /** Players who left by resigning (a subset of `eliminated`). */
    resigned: z.array(z.string()).optional(),
    attacksThisTurn: z.number(),
    fortifiesThisTurn: z.number(),
    roundsPlayed: z.number(),
    armiesToPlace: z.record(z.string(), z.number()),
    /**
     * The most-recent tail of the event log (WIRE_EVENT_LIMIT entries), for the
     * in-game "recent activity" glance. The FULL log is served by
     * `GET /games/:id/events`; the time-machine reconstructs from that, mapping
     * a tail row to its absolute index via `offset = eventCount - events.length`.
     */
    events: z.array(z.unknown()).optional(),
    /**
     * True total number of events in the persisted log. Use for totals and to
     * compute the tail offset (`eventCount - events.length`) for absolute
     * indexing into the full log.
     */
    eventCount: z.number().optional(),
    /** Monotonic Game.stateVersion used to reject delayed frames on clients. */
    stateVersion: z.number().optional(),
    // Optional fields that may appear in newer game rows
    lastConquest: z
      .object({
        fromTerritoryId: z.string(),
        toTerritoryId: z.string(),
        armiesAlreadyMoved: z.number(),
      })
      .nullable()
      .optional(),
    cards: z.record(z.string(), z.array(z.unknown())).optional(),
    cardTurnIns: z.number().optional(),
    bonusArmies: z.record(z.string(), z.number()).optional(),
    conqueredThisTurn: z.boolean().optional(),
    mustTurnInCards: z.boolean().optional(),
    initialTerritories: z.array(TerritoryWireSchema).optional(),
    // REGRESSION GUARD: `map` must NEVER appear on the wire.
    // normaliseStateForWire strips it; any path that accidentally passes it
    // through will fail this parse immediately.
    map: z.never().optional(),
  })
  .passthrough()

// ─── GameResponseSchema ───────────────────────────────────────────────────────
// Mirrors the GameResponse interface in index.ts.

const GameStatusSchema = z.enum(['LOBBY', 'STARTING', 'ACTIVE', 'PAUSED', 'COMPLETE', 'ABANDONED'])
const GameModeSchema = z.enum(['PASS_AND_PLAY', 'LIVE', 'SOLO'])

const UserPickSchema = z.object({
  id: z.string(),
  username: z.string(),
  displayName: z.string(),
})

const GamePlayerResponseSchema = z.object({
  vacation: z.object({ startedAt: z.string(), endsAt: z.string(), message: z.string().nullable() }).nullable().optional(),
  id: z.string(),
  userId: z.string(),
  user: UserPickSchema,
  playerIndex: z.number(),
  slot: z.number(),
  colorSlot: z.number().nullable(),
  displayName: z.string(),
  color: z.string().nullable(),
  isReady: z.boolean(),
  isBot: z.boolean(),
  botProfile: z.string().nullable(),
  botPersonality: z.string().nullable(),
  takenOverFromUserId: z.string().nullable().optional(),
  takenOverAt: z.string().nullable().optional(),
  takenOverReason: z.enum(['turn-timeout', 'abandoned']).nullable().optional(),
  turnCount: z.number().optional(),
  averageTurnSeconds: z.number().nullable().optional(),
  score: z.number().nullable(),
  rank: z.number().nullable(),
  joinedAt: z.string(),
})

const GameInvitationResponseSchema = z.object({
  id: z.string(),
  status: z.enum(['pending', 'accepted', 'revoked', 'declined']),
  sentAt: z.string(),
  respondedAt: z.string().nullable(),
  slot: z.number().nullable(),
  user: UserPickSchema,
  notifiable: z.boolean(),
})

const GameResultSnapshotPlacementSchema = z.object({
  slot: z.number(),
  playerIndex: z.number(),
  userId: z.string(),
  username: z.string(),
  displayName: z.string(),
  isBot: z.boolean(),
  botProfile: z.string().nullable(),
  personality: z.string().nullable(),
  strategyVersion: z.number(),
  score: z.number().nullable(),
  placement: z.number(),
})

const GameResultSnapshotResponseSchema = z.object({
  schemaVersion: z.number(),
  winnerSlot: z.number().nullable(),
  endReason: z.enum(['last-standing', 'stalled', 'resign', 'admin-complete']),
  completedAt: z.string(),
  placements: z.array(GameResultSnapshotPlacementSchema),
  diceLuck: z.object({
    players: z.array(z.object({
      playerId: z.string(),
      rollCount: z.number(),
      attackRollCount: z.number(),
      defenseRollCount: z.number(),
      netSwing: z.number(),
      attackSwing: z.number(),
      defenseSwing: z.number(),
      variance: z.number(),
      zScore: z.number().nullable(),
      percentile: z.number().nullable(),
      verdict: z.enum(['brutal', 'unlucky', 'average', 'lucky', 'blessed']).nullable(),
    })),
  }).nullable().optional(),
})

export const GameResponseSchema = z.object({
  id: z.string(),
  adminId: z.string(),
  inviteCode: z.string().nullable(),
  name: z.string().nullable(),
  status: GameStatusSchema,
  mode: GameModeSchema,
  minPlayers: z.number(),
  maxPlayers: z.number(),
  autoStart: z.boolean(),
  bootAfterSeconds: z.number().int().default(432000),
  bootableAt: z.string().nullable().default(null),
  botRosterVersion: z.number(),
  currentTurn: z.number(),
  currentPlayerIndex: z.number(),
  state: WireGameStateSchema.nullable(),
  resultSnapshot: GameResultSnapshotResponseSchema.nullable(),
  // GET /games list, finished team games only.
  winnerTeam: z.enum(['A', 'B', 'C', 'D']).nullable().optional(),
  winnerSlots: z.array(z.number()).optional(),
  isPublic: z.boolean(),
  isRanked: z.boolean().default(true),
  mapId: z.string().nullable(),
  setupConfig: z.record(z.string(), z.unknown()).nullable(),
  players: z.array(GamePlayerResponseSchema),
  invitations: z.array(GameInvitationResponseSchema),
  viewerInvitationStatus: z.enum(['pending']).nullable(),
  startedAt: z.string().nullable(),
  turnStartedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastChatMessageId: z.string().nullable(),
  unreadSecretCount: z.number(),
})

// ─── ServerEventSchema ────────────────────────────────────────────────────────
// Discriminated union mirroring the ServerEvent type in index.ts —
// covering the event types that have runtime schema consumers.

export const ServerEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('YOUR_TURN'),
    /** Private event: the authenticated user's own turn changed. */
    gameId: z.string(),
    gameName: z.string(),
    turn: z.number().nullable(),
    occurredAt: z.number(),
  }),
  z.object({
    type: z.literal('GAME_UPDATED'),
    gameId: z.string().optional(),
    game: GameResponseSchema,
  }),
  z.object({
    type: z.literal('GAME_STATE_PATCH'),
    gameId: z.string(),
    state: WireGameStateSchema,
  }),
  z.object({
    type: z.literal('GAME_COMPLETE'),
    gameId: z.string().optional(),
    game: GameResponseSchema,
    winnerSlot: z.string().optional(),
    tied: z.array(z.string()).optional(),
  }),
])

// ─── GameEventsResponseSchema ─────────────────────────────────────────────────
// Mirrors the GameEventsResponse interface in index.ts.
// Used by GET /games/:id/events — the full (untruncated) event log endpoint.

export const GameEventsResponseSchema = z.object({
  events: z.array(z.unknown()),
  eventCount: z.number(),
  /** Offset the server sliced from (delta fetch). Absent on older responses. */
  since: z.number().optional(),
})
